import { CSSProperties, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@libs/zapi-ui/ds/components/button";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import { useChapterStore } from "@/zapi-frontend/stores/chapter";
import { useProjectSidebarStore } from "@/zapi-frontend/stores/projectSidebarStore";
import { useCommentStore } from "@/zapi-frontend/widgets/CommentsIsland/model/store/commentsStore";
import { getChapterTools } from "../lib/chapterTools";
import { ChapterToolPanel } from "./ChapterToolPanel";
import { WidgetCard, WidgetCardHeader, WidgetCardSeparator } from "./WidgetCard";

function ChapterToolButtons({ disabled }: { disabled: boolean }) {
  const { t } = useTranslation("projectHome");
  const { openSidebar, toggleHandler } = useProjectSidebarStore();
  const { activeChapter } = useChapterStore();
  const commentCount = useCommentStore((s) => s.commentsStorage[String(activeChapter?.id ?? "")]?.length ?? 0);
  const tools = getChapterTools({ commentCount });

  return (
    <>
      {tools.map((tool) => {
        const isOpen = openSidebar === tool.value;
        return (
          <Button
            key={tool.value}
            variant="ghost"
            data-testid={`sidebar-${tool.value}-button`}
            aria-pressed={isOpen}
            aria-label={
              tool.count === undefined
                ? t(tool.labelKey)
                : t("chapterHeader.toolWithCount", { label: t(tool.labelKey), count: tool.count })
            }
            title={t(tool.titleKey)}
            disabled={disabled}
            onClick={() => toggleHandler(tool.value)}
            className="gap-1.5 px-2.5 text-muted-foreground aria-pressed:text-foreground"
          >
            <tool.icon weight={isOpen ? "fill" : "regular"} />
            <span className="hidden @2xl/widget:inline">{t(tool.labelKey)}</span>
            {tool.count !== undefined && (
              <span className="typo-caption min-w-4 rounded-full bg-foreground/8 px-1 text-center leading-4 text-foreground">
                {tool.count}
              </span>
            )}
          </Button>
        );
      })}
    </>
  );
}

export function ChapterTools({ disabled, menu }: { disabled: boolean; menu?: ReactNode }) {
  return (
    <>
      <ChapterToolButtons disabled={disabled} />
      {menu && (
        <>
          <WidgetCardSeparator />
          {menu}
        </>
      )}
    </>
  );
}

type TChapterWidgetProps = {
  header?: { title: string; actions: ReactNode };
  stickyTop?: string;
  fill?: boolean;
  toolPanel: ReactNode;
  children: ReactNode;
};

export function ChapterWidget({ header, stickyTop, fill, toolPanel, children }: TChapterWidgetProps) {
  const openSidebar = useProjectSidebarStore((state) => state.openSidebar);
  const closeSidebar = useProjectSidebarStore((state) => state.closeHandler);
  const pinned = !fill && !!stickyTop;

  return (
    <WidgetCard
      data-testid="chapter-widget"
      className={cn(fill && "min-h-0 flex-1")}
      style={pinned ? ({ "--widget-sticky-top": stickyTop } as CSSProperties) : undefined}
    >
      {header && (
        <WidgetCardHeader
          title={header.title}
          titleOverflow="wrap"
          stickyTop={fill ? undefined : stickyTop}
          actions={header.actions}
        />
      )}
      <div
        data-slot="chapter-widget-body"
        className={cn(
          "relative min-w-0",
          !header && "*:data-[slot=chapter-tool-panel]:rounded-se-2xl",
          fill ? "min-h-0 flex-1 overflow-hidden rounded-b-2xl" : "has-data-[slot=chapter-tool-panel]:min-h-60"
        )}
      >
        {children}
        <ChapterToolPanel open={!!openSidebar} pinned={pinned} onClose={closeSidebar}>
          {toolPanel}
        </ChapterToolPanel>
      </div>
    </WidgetCard>
  );
}
